import React from "react";
import { UnlinkIcon } from "assets/svg/unlink";
import { inject, observer } from "mobx-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Box from "react-raster";
import { Button, Icon, Input, Modal, Text, ColorPicker, ConfirmationDialog } from "src/components";
import { theme } from "src/config";
import { Tag } from "src/models";
import { TagStore, UserStore, AuthStore } from "src/stores";
import styled from "styled-components";
import { Card } from "./card";
import { useClickOutside } from "src/util/util";

export interface ITagCardProps {
  data:       Tag;
  tagStore?:  TagStore;
  userStore?: UserStore;
  authStore?: AuthStore;
  onClick? (): void;
  onUnlink?(): void;
}

export const TagCard = inject("tagStore", "userStore", "authStore")(observer((props: ITagCardProps): JSX.Element => {
    const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
    const { data: tag, tagStore, authStore } = props;
    const { t, i18n } = useTranslation();
    const { getCurUser: currentUser } = authStore;

    const cardRef = React.useRef<HTMLDivElement>(null);

    useClickOutside(cardRef, () => {
      if(tagStore.getActive && tag.id === tagStore.getActive.id && !showConfirmDialog) {
        tagStore.setActive(null, true);
      }
    });

    return (
      <Card
        ref  = {cardRef}
        body = {
          <Box
            display        = "flex"
            justifyContent = "space-between"
            flexDirection  = "row"
          >
            <Text size="default">{tag.title}</Text>{" "}
            <Icon iconId="style" size="xxlarge" stroke={tag.color} fill={tag.color} />
          </Box>
        }
        expandedBody={
          <CardContainer>
            <TagContainerHead>
              <Input
                value    = {tag.title}
                onBlur   = {() => tag.save()}
                label    = {t("name")}
                onChange = {(val) =>
                  tag.set("title", val)
                }
                editOnFocus
                autoFocus
              />
            </TagContainerHead>
            <TagCardContent>
              <ColorPicker
                value    = {tag.color||'#607d8b'}
                onClose = {(val) => tag.set("color", val, true)}
                label    = {t("color")} 
              />
            </TagCardContent>
            <ButtonContainer>
              <Modal show={showConfirmDialog}>
                <ConfirmationDialog
                  onConfirm={async () => {
                    try {
                      await tag.remove();
                      tagStore.setActive(null);
                      toast.success(t("remove_tag"), {
                        icon: <Icon size="medium" iconId="delete"/>
                      });
                    } catch (err) {
                      console.error("Failed to remove tag", tag)
                    }
                  }}
                  onCancel = {() => setShowConfirmDialog(false)}
                  label    = {t("confirm_action")}
                  message  = {t("confirm_action_tag_message")}
                />
              </Modal>
              <Button variant="icon" onClick={() => setShowConfirmDialog(true)}>
                <Icon iconId="delete" size="large" color={theme.color.grayDark} />
              </Button>
              {props.onUnlink && (
                <Button variant="icon" onClick={() => props.onUnlink()}>
                  <UnlinkIcon color={theme.color.grayDark} />
                </Button>
              )}
            </ButtonContainer>
          </CardContainer>
        }
        footer={
          <Box>
            <Text size="small">{`${t(
              "created_by"
            )}: ${props.userStore.getItemByID(tag.created_by)?.displayName}`}</Text>
          </Box>
        }
        key      = {tag.id}
        editable = {currentUser.canEdit}
        onClick  = {props.onClick || ((e) => tagStore.setActive(tag))}
        height   = "auto"
        width    = "25rem"
        expanded = {Boolean(
          tagStore.getActive && tag.id === tagStore.getActive.id
        )}
      />
    );
  })
);


const ButtonContainer = styled.div`
  display:         flex;
  width:           100%;
  justify-content: space-between;
  flex-direction:  row-reverse;
`;

const CardContainer = styled.div`
  display:        flex;
  flex-direction: column;
  gap:            1rem;
`;

const TagContainerHead = styled.div`
  flex-direction: column;
  display:        flex;
  gap:            1.5rem;
`;

const TagCardContent = styled.div`
  display:        grid;
  flex-direction: column;
  gap:            1rem;
  margin:         1rem 0rem;
`;
